/**
 * The library's Electre NG account: credentials, environment, availability in
 * the external search, and a connection test. The password is write-only.
 */

import { useId, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, Info, Loader2, PlugZap } from "lucide-react";
import {
  useClearElectreSettings,
  useElectreSettings,
  useSaveElectreSettings,
  useTestElectreConnection,
} from "@/api/hooks";
import type { ElectreEnvironment, ElectreSettings } from "@/api/types";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const ENVIRONMENTS: Array<{ value: ElectreEnvironment; label: string; hint: string }> = [
  {
    value: "production",
    label: "Production",
    hint: "Le catalogue Electre et vos vrais paniers.",
  },
  {
    value: "demo",
    label: "Recette",
    hint: "Environnement de démonstration d'Electre, avec un compte de test distinct.",
  },
];

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500";

export function ElectreSettingsPage() {
  const settings = useElectreSettings();

  return (
    <div className="max-w-3xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Electre</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Reliez l&apos;abonnement Electre NG de la bibliothèque pour rechercher des notices dans la
          base Electre et importer les paniers envoyés depuis Electre. Le compte API est fourni par
          Electre (api@electre.com).
        </p>
      </header>

      {settings.isLoading ? (
        <div aria-busy="true" aria-label="Chargement des réglages Electre" className="space-y-4">
          <div className="h-24 animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-800" />
          <div className="h-80 animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-800" />
        </div>
      ) : settings.error || !settings.data ? (
        <p
          role="alert"
          className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          <AlertCircle className="h-4 w-4" aria-hidden />
          Impossible de charger les réglages Electre.
        </p>
      ) : (
        <>
          <ConnectionStatus settings={settings.data} />
          <AccountForm settings={settings.data} />
          {settings.data.origin === "database" && <ClearAccount />}
        </>
      )}
    </div>
  );
}

function ConnectionStatus({ settings }: { settings: ElectreSettings }) {
  const test = useTestElectreConnection();
  const checkedAt = settings.last_checked_at
    ? new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(
        new Date(settings.last_checked_at)
      )
    : null;

  let tone: "ok" | "error" | "neutral" = "neutral";
  let title = "Aucun compte Electre";
  let detail = "Saisissez le compte API ci-dessous pour activer le connecteur.";
  if (settings.configured) {
    if (settings.last_status === "active") {
      tone = "ok";
      title = "Connecté à Electre";
      detail = checkedAt ? `Dernière vérification le ${checkedAt}.` : "";
    } else if (settings.last_status === "error") {
      tone = "error";
      title = "La connexion à Electre a échoué";
      detail = settings.last_error ?? "";
    } else {
      title = "Compte enregistré, connexion non vérifiée";
      detail = "Testez la connexion pour vérifier les identifiants.";
    }
  }

  return (
    <section
      aria-labelledby="electre-status"
      className={cn(
        "rounded-2xl border p-5",
        tone === "ok" &&
          "border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/60 dark:bg-emerald-950/30",
        tone === "error" && "border-red-200 bg-red-50/60 dark:border-red-900/60 dark:bg-red-950/30",
        tone === "neutral" && "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 gap-3">
          {tone === "ok" ? (
            <CheckCircle2
              className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400"
              aria-hidden
            />
          ) : tone === "error" ? (
            <AlertCircle
              className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400"
              aria-hidden
            />
          ) : (
            <PlugZap className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" aria-hidden />
          )}
          <div className="min-w-0">
            <h2 id="electre-status" className="text-sm font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
            {detail && (
              <p className="mt-0.5 text-sm break-words text-gray-600 dark:text-gray-300">
                {detail}
              </p>
            )}
            {settings.origin === "environment" && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Compte défini par la configuration du serveur (variables ELECTRE_*). Un compte
                enregistré ici le remplace.
              </p>
            )}
            {settings.configured && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Recherche externe :{" "}
                {settings.active ? "Electre est proposé" : "Electre n'est pas proposé"} ·{" "}
                <Link
                  to="/interop/electre-baskets"
                  className="text-primary underline-offset-2 hover:underline"
                >
                  Voir les paniers
                </Link>
              </p>
            )}
          </div>
        </div>
        {settings.configured && (
          <Button
            type="button"
            variant="outline"
            disabled={test.isPending}
            onClick={() => {
              test.mutate();
            }}
          >
            {test.isPending && <Loader2 className="animate-spin" aria-hidden />}
            Tester la connexion
          </Button>
        )}
      </div>
      {/* The outcome shows in the card above; this announces it to screen readers */}
      <p role="status" className="sr-only">
        {test.isPending ? "" : (test.data?.message ?? test.error?.message ?? "")}
      </p>
    </section>
  );
}

function AccountForm({ settings }: { settings: ElectreSettings }) {
  const save = useSaveElectreSettings();
  const formId = useId();
  const [environment, setEnvironment] = useState<ElectreEnvironment>(
    settings.environment ?? "production"
  );
  const [username, setUsername] = useState(
    settings.origin === "database" ? (settings.username ?? "") : ""
  );
  const [password, setPassword] = useState("");
  const [active, setActive] = useState(settings.origin === "database" ? settings.active : true);
  const [saved, setSaved] = useState(false);

  // A stored password only counts for an account saved here
  const hasStoredPassword = settings.origin === "database" && settings.has_password;

  // The confirmation describes the saved form: any edit makes it stale
  const edited = () => {
    setSaved(false);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(
      { environment, username, password, active },
      {
        onSuccess: () => {
          setPassword("");
          setSaved(true);
        },
      }
    );
  };

  return (
    <form
      onSubmit={submit}
      aria-labelledby={`${formId}-title`}
      className="space-y-6 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900"
    >
      <h2 id={`${formId}-title`} className="text-base font-semibold text-gray-900 dark:text-white">
        Compte API
      </h2>

      <fieldset>
        <legend className="mb-1.5 text-sm text-gray-700 dark:text-gray-200">Environnement</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          {ENVIRONMENTS.map((option) => (
            <label
              key={option.value}
              className={cn(
                "cursor-pointer rounded-xl border px-4 py-3 transition-colors",
                "has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-2",
                environment === option.value
                  ? "border-primary/50 bg-primary/5 dark:bg-primary/10"
                  : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
              )}
            >
              <span className="flex items-center gap-2.5">
                <input
                  type="radio"
                  name={`${formId}-environment`}
                  value={option.value}
                  checked={environment === option.value}
                  onChange={() => {
                    setEnvironment(option.value);
                    edited();
                  }}
                  className="accent-primary h-4 w-4"
                />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {option.label}
                </span>
              </span>
              <span className="mt-1 block pl-6.5 text-xs text-gray-500 dark:text-gray-400">
                {option.hint}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label
            htmlFor={`${formId}-username`}
            className="mb-1.5 block text-sm text-gray-700 dark:text-gray-200"
          >
            Identifiant
          </label>
          <input
            id={`${formId}-username`}
            value={username}
            onChange={(event) => {
              setUsername(event.target.value);
              edited();
            }}
            required
            maxLength={100}
            autoComplete="off"
            spellCheck={false}
            className={inputClass}
          />
        </div>
        <div>
          <label
            htmlFor={`${formId}-password`}
            className="mb-1.5 block text-sm text-gray-700 dark:text-gray-200"
          >
            Mot de passe
          </label>
          <input
            id={`${formId}-password`}
            type="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              edited();
            }}
            required={!hasStoredPassword}
            maxLength={128}
            // Keeps the browser from filling in the administrator's own Lumen password
            autoComplete="new-password"
            placeholder={hasStoredPassword ? "••••••••" : undefined}
            aria-describedby={`${formId}-password-hint`}
            className={inputClass}
          />
          <p
            id={`${formId}-password-hint`}
            className="mt-1 text-xs text-gray-500 dark:text-gray-400"
          >
            {hasStoredPassword
              ? "Enregistré et chiffré. Laissez vide pour le conserver."
              : "Chiffré à l'enregistrement, jamais réaffiché."}
          </p>
        </div>
      </div>

      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={active}
          onChange={(event) => {
            setActive(event.target.checked);
            edited();
          }}
          className="accent-primary mt-0.5 h-4 w-4"
        />
        <span>
          <span className="block text-sm font-medium text-gray-900 dark:text-white">
            Proposer Electre dans la recherche externe
          </span>
          <span className="block text-xs text-gray-500 dark:text-gray-400">
            Les catalogueurs pourront y chercher des notices, à côté de la BnF. Les paniers restent
            accessibles même décoché.
          </span>
        </span>
      </label>

      {settings.environment === null && settings.origin === "environment" && (
        <p className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          Le serveur utilise des adresses Electre personnalisées ; un compte enregistré ici
          utilisera l&apos;environnement choisi ci-dessus.
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
        {save.error && (
          <p role="alert" className="mr-auto text-sm text-red-700 dark:text-red-400">
            {save.error.message}
          </p>
        )}
        {saved && !save.error && (
          <p role="status" className="mr-auto text-sm text-emerald-700 dark:text-emerald-400">
            Compte enregistré. Testez la connexion pour le vérifier.
          </p>
        )}
        <Button type="submit" disabled={save.isPending}>
          {save.isPending && <Loader2 className="animate-spin" aria-hidden />}
          Enregistrer
        </Button>
      </div>
    </form>
  );
}

function ClearAccount() {
  const clear = useClearElectreSettings();
  const [confirming, setConfirming] = useState(false);

  return (
    <section
      aria-labelledby="electre-clear"
      className="rounded-xl border border-red-200 bg-white p-5 dark:border-red-900/60 dark:bg-gray-900"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="max-w-md">
          <h2 id="electre-clear" className="text-sm font-medium text-gray-900 dark:text-white">
            Supprimer le compte enregistré
          </h2>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Electre est retiré de la recherche externe et les paniers ne sont plus accessibles. Les
            notices déjà importées sont conservées.
          </p>
        </div>
        {confirming ? (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setConfirming(false);
              }}
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={clear.isPending}
              onClick={() => {
                clear.mutate(undefined, {
                  onSettled: () => {
                    setConfirming(false);
                  },
                });
              }}
            >
              {clear.isPending && <Loader2 className="animate-spin" aria-hidden />}
              Confirmer la suppression
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setConfirming(true);
            }}
          >
            Supprimer
          </Button>
        )}
      </div>
      {clear.error && (
        <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-400">
          {clear.error.message}
        </p>
      )}
    </section>
  );
}
